/** A bare calendar date, which is what games.date holds (it is a varchar). */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A clip's game date, rendered the way v1 renders it:
 * `moment(game_date).format("MMMM DD, YYYY")` in the viewer's own timezone
 * (src/Pages/PersonalChannel/MatchCardv2/index.tsx:205).
 *
 * moment is banned from v2 (src/v2/__tests__/boundaries.test.ts), so this is
 * Intl, but matching v1 takes more than swapping the formatter.
 *
 * THE DATE-ONLY BRANCH IS THE WHOLE POINT. game_date arrives as "2025-12-14",
 * a calendar date with no time and no zone, and that is the only shape it ever
 * has. `new Date("2025-12-14")` reads it as midnight UTC, so rendering it in
 * local time moves it to the 13th for every viewer west of UTC, which is
 * essentially the entire audience. moment reads a bare date as local midnight
 * and therefore shows the 14th everywhere. Building the Date from the parts
 * does the same, so the date a game was played is the date shown.
 *
 * A full instant ("2026-01-05T23:30:00Z") is a real point in time and is
 * parsed normally, so it lands on whatever day it is for the viewer, exactly
 * as moment would.
 */
export function formatClipDate(dateStr?: string): string {
  if (!dateStr) return "";
  const parts = DATE_ONLY.exec(dateStr);
  const date = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

/**
 * Clip length as m:ss. The search API returns it as a seconds string with two
 * decimals ("549.00"), summed from the clip's video chunks.
 */
export function formatClipDuration(duration?: string | number): string {
  const seconds = Math.round(Number(duration));
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/** Clips are marked New for a week after they are cut. */
export const NEW_CLIP_DAYS = 7;

/**
 * Whether a clip was created recently enough to badge as New.
 *
 * Keyed on when the clip was created, not the game date: a game from last
 * season can be cut into a new clip today, and that clip is new to the viewer.
 * Returns false when the payload carries no creation timestamp, so a missing
 * field shows nothing rather than badging everything.
 */
export function isNewClip(createdAt?: string | null, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  const ageDays = (now - created) / 86400000;
  return ageDays >= 0 && ageDays < NEW_CLIP_DAYS;
}
